import React, { useState, useEffect } from 'react';
import './LoginModal.css';
import { FaTimes, FaEnvelope, FaLock, FaEye, FaEyeSlash, FaGoogle, FaFacebookF } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

export default function LoginModal({ isOpen, onClose }) {
  const { login, register } = useAuth();
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    confirmPassword: '',
    telefono: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isRegisterMode, setIsRegisterMode] = useState(false);

  // Cerrar modal con ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      let result;
      
      if (isRegisterMode) {
        // Validaciones para registro
        if (formData.password !== formData.confirmPassword) {
          setError('Las contraseñas no coinciden');
          setIsLoading(false);
          return;
        }
        
        if (formData.password.length < 6) {
          setError('La contraseña debe tener al menos 6 caracteres');
          setIsLoading(false);
          return;
        }
        
        if (!formData.nombre.trim()) {
          setError('El nombre es requerido');
          setIsLoading(false);
          return;
        }
        
        result = await register({
          nombre: formData.nombre,
          email: formData.email,
          password: formData.password,
          telefono: formData.telefono
        });
      } else {
        // Login
        result = await login(formData.email, formData.password);
      }
      
      if (result.success) {
        // Limpiar formulario
        setFormData({ 
          nombre: '', 
          email: '', 
          password: '', 
          confirmPassword: '', 
          telefono: '' 
        });
        setIsRegisterMode(false); // Volver al modo login
        
        // Cerrar modal después de un pequeño delay para permitir la redirección
        setTimeout(() => {
          onClose();
        }, 200);
      } else {
        setError(result.message || `Error al ${isRegisterMode ? 'registrarse' : 'iniciar sesión'}`);
      }
    } catch (error) {
      setError('Error de conexión. Inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    console.log(`Iniciando sesión con ${provider}`);
    // Aquí iría la lógica de autenticación social
  };

  if (!isOpen) return null;

  return (
    <div className="login-modal-overlay" onClick={onClose}>
      <div className="login-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="login-modal-header">
          <div className="login-modal-logo">
            <img src="/images/RecomiendaT-logo.png" alt="RecomiendaT" className="login-logo-img" />
          </div>
          <button className="login-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Contenido */}
        <div className="login-modal-content">
          <h2 className="login-modal-title">
            {isRegisterMode ? 'CREAR CUENTA' : 'INICIAR SESIÓN'}
          </h2>
          <p className="login-modal-subtitle">
            {isRegisterMode ? 'Únete a la comunidad RecomiendaT' : 'Accede a tu cuenta de RecomiendaT'}
          </p>

          {/* Mensaje de error */}
          {error && (
            <div className="login-error-message">
              {error}
            </div>
          )}

          {/* Formulario */}
          <form className="login-form" onSubmit={handleSubmit}>
            {/* Campo nombre (solo en registro) */}
            {isRegisterMode && (
              <div className="login-input-group">
                <div className="login-input-container">
                  <FaEnvelope className="login-input-icon" />
                  <input
                    type="text"
                    name="nombre"
                    placeholder="Nombre completo"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    className="login-input"
                    required
                  />
                </div>
              </div>
            )}

            <div className="login-input-group">
              <div className="login-input-container">
                <FaEnvelope className="login-input-icon" />
                <input
                  type="email"
                  name="email"
                  placeholder="Correo electrónico"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="login-input"
                  required
                />
              </div>
            </div>

            {/* Campo teléfono (solo en registro) */}
            {isRegisterMode && (
              <div className="login-input-group">
                <div className="login-input-container">
                  <FaEnvelope className="login-input-icon" />
                  <input
                    type="tel"
                    name="telefono"
                    placeholder="Teléfono (opcional)"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    className="login-input"
                  />
                </div>
              </div>
            )}

            <div className="login-input-group">
              <div className="login-input-container">
                <FaLock className="login-input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Contraseña"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="login-input"
                  required
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {/* Campo confirmar contraseña (solo en registro) */}
            {isRegisterMode && (
              <div className="login-input-group">
                <div className="login-input-container">
                  <FaLock className="login-input-icon" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    placeholder="Confirmar contraseña"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="login-input"
                    required
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
            )}

            {!isRegisterMode && (
              <div className="login-forgot-password">
                <button 
                  type="button"
                  className="login-forgot-link"
                  onClick={() => {
                    // TODO: Implementar recuperación de contraseña
                    console.log('Recuperar contraseña');
                  }}
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
            )}

            <button 
              type="submit" 
              className={`login-submit-btn ${isLoading ? 'loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading 
                ? (isRegisterMode ? 'CREANDO CUENTA...' : 'INICIANDO SESIÓN...') 
                : (isRegisterMode ? 'CREAR CUENTA' : 'INICIAR SESIÓN')
              }
            </button>
          </form>

          {/* Divisor */}
          <div className="login-divider">
            <span className="login-divider-text">O continúa con</span>
          </div>

          {/* Botones sociales */}
          <div className="login-social-buttons">
            <button 
              className="login-social-btn google"
              onClick={() => handleSocialLogin('Google')}
            >
              <FaGoogle className="login-social-icon" />
              Google
            </button>
            <button 
              className="login-social-btn facebook"
              onClick={() => handleSocialLogin('Facebook')}
            >
              <FaFacebookF className="login-social-icon" />
              Facebook
            </button>
          </div>

          {/* Footer */}
          <div className="login-modal-footer">
            <p className="login-register-text">
              {isRegisterMode ? (
                <>
                  ¿Ya tienes cuenta?{' '}
                  <button 
                    type="button"
                    className="login-register-link"
                    onClick={() => {
                      setIsRegisterMode(false);
                      setError('');
                      setFormData({ 
                        nombre: '', 
                        email: '', 
                        password: '', 
                        confirmPassword: '', 
                        telefono: '' 
                      });
                    }}
                  >
                    Inicia sesión aquí
                  </button>
                </>
              ) : (
                <>
                  ¿No tienes cuenta?{' '}
                  <button 
                    type="button"
                    className="login-register-link"
                    onClick={() => {
                      setIsRegisterMode(true);
                      setError('');
                    }}
                  >
                    Regístrate aquí
                  </button>
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
